import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import InputPassword from 'components/form/InputPassword';
import InputField from 'components/form/InputField';
import styled from 'styled-components';
import { theme } from 'utils/style/theme';
import logoIcon from 'utils/icon/carpe.png';
import { Checkbox } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import PrimaryButton from 'components/button/PrimaryButton';
import { Link, useNavigate } from 'react-router-dom';
import { userApi } from 'apis/user/userApi';
import { ToastContainer, toast } from 'react-toastify';
import { IAccount } from 'utils/types/common';
import { useAppDispatch } from 'hooks/redux';
import { setUserInfor } from 'store/slices/userSlice';
import { cookieServices } from 'services/cookieServices';
import { useCookies } from 'react-cookie';

//* 로그인 페이지

const Login: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({});
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [cookies, setCookie] = useCookies([
    'lang',
    'server',
    'managementAuth',
    'platformAuth',
  ]);
  const [isRememberAccount, setIsRememberAccount] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit: SubmitHandler<any> = async (data) => {
    setLoading(true);
    const response = await userApi.login(data.email, data.password);
    if (response.isSuccess) {
      const userData: IAccount = {
        id: response.account.id,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        user_id: response.account.id,
        master_auth: 1,
        email: response.account.email,
        userName: response.account.userName,
        password: response.account.password,
        organization: response.account.organization,
        role: response.account.role,
        verified: response.account.verified,
        projectAuths: response.account.projectAuths,
      };

      //  Set user information to cookie.
      cookieServices.setUserInfo(userData, isRememberAccount);

      dispatch(setUserInfor(userData));

      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

      // TODO 어떤 언어지.
      setCookie('lang', 'KO', {
        expires: oneMonthFromNow,
      });

      window.location.replace('/');
    } else {
      toast(response.error);
    }
    setLoading(false);
  };

  const handCheckbox = (e: CheckboxChangeEvent) => {
    setIsRememberAccount(e.target.checked);
  };

  return (
    <>
      <PageWrapper>
        <LoginWrapper>
          <LogoWrapper>
            <img src={logoIcon} alt='logo' className='logo' />
          </LogoWrapper>
          <FormWrapper>
            <TitleWrapper>
              <h1>Welcome to Kevin!</h1>
              <h2>
                Sign in to access your amazing story game <br /> contents.
              </h2>
            </TitleWrapper>
            <form onSubmit={handleSubmit(onSubmit)} className='space-y-[12px]'>
              <InputWrapper>
                <InputField
                  register={register}
                  errors={errors}
                  title={`Email`}
                  name='email'
                  type='string'
                  placeHolder='Enter your email'
                  errText='This field is required'
                  opts={{
                    required: 'This field is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                    maxLength: {
                      value: 100,
                      message: 'Email address is too long',
                    },
                    minLength: {
                      value: 10,
                      message: 'Email address is too short',
                    },
                  }}
                />
                <div>
                  <InputPassword
                    register={register}
                    errors={errors}
                    title={`Password`}
                    name='password'
                    type='string'
                    placeHolder='Enter your password'
                    errText='This field is required'
                    opts={{
                      required: 'This field is required',
                      maxLength: {
                        value: 100,
                        message: '',
                      },
                      minLength: {
                        value: 2,
                        message: '',
                      },
                    }}
                  />
                  <div>
                    <CheckboxWrapper onChange={handCheckbox}>
                      Keep me signed in
                    </CheckboxWrapper>
                  </div>
                </div>
              </InputWrapper>
              <FooterFormWrapper>
                <button className='submit-btn'>
                  <PrimaryButton
                    text='Sign in'
                    height='40px'
                    width='100%'
                    bgColor={theme.colors.blue_500}
                    textColor='#ffffff'
                    fontWeight={theme.fontWeights.medium}
                    fontSize={theme.fontSizes.normal}
                    border={`1px solid ${theme.colors.blue_500}`}
                    rounded='6px'
                    hoverBgColor={theme.colors.blue_600}
                    activeBgColor={theme.colors.blue_800}
                    selectedBgColor={theme.colors.blue_700}
                    loading={loading}
                  />
                </button>
                <div>
                  <Link to='/signup'>
                    <PrimaryButton
                      text='Join Organization'
                      height='40px'
                      width='100%'
                      bgColor='#ffffff'
                      textColor={theme.colors.dark_400}
                      fontWeight={theme.fontWeights.medium}
                      fontSize={theme.fontSizes.normal}
                      border={`1px solid ${theme.colors.gray_6}`}
                      rounded='6px'
                      hoverBgColor={theme.colors.gray_7}
                      activeBgColor={theme.colors.gray_8}
                      selectedBgColor={theme.colors.gray_8}
                    />
                  </Link>
                </div>
                <div>
                  <Link to='/create-organization'>
                    <PrimaryButton
                      text='Create Organization'
                      height='40px'
                      width='100%'
                      bgColor={theme.colors.red_400} // 변경된 배경색
                      textColor='#ffffff' // 변경된 텍스트 색상
                      fontWeight={theme.fontWeights.medium}
                      fontSize={theme.fontSizes.normal}
                      border={`1px solid ${theme.colors.red_400}`} // 변경된 테두리 색상
                      rounded='6px'
                      hoverBgColor={theme.colors.red_500} // 변경된 호버 배경색
                      activeBgColor={theme.colors.red_600} // 변경된 활성화 배경색
                      selectedBgColor={theme.colors.red_700} // 변경된 선택된 배경색
                    />
                  </Link>
                </div>
                <p className='forget-text'>Forget your password?</p>
              </FooterFormWrapper>
            </form>
          </FormWrapper>
        </LoginWrapper>
      </PageWrapper>
      <ToastContainer />
    </>
  );
};

export default Login;

const PageWrapper = styled.div`
  background-color: #fbfbfb;
  width: 100%;
  height: 100vh;
  a {
    text-decoration: none;
  }
`;

const LoginWrapper = styled.div`
  margin: 0 auto;
  width: 80%;
  @media (min-width: 1440px) {
    width: 40%;
  }
`;

const FormWrapper = styled.div`
  background: #ffffff;
  border: 1px solid ${theme.colors.gray_8};
  border-radius: 32px;
  padding: 48px;
`;

const TitleWrapper = styled.div`
  margin-bottom: 16px;
  text-align: center;
  h1 {
    font-size: ${theme.fontSizes.xxl};
    color: ${theme.colors.dark_500};
    font-weight: ${theme.fontWeights.medium};
  }
  h2 {
    font-size: ${theme.fontSizes.normal};
    color: ${theme.colors.dark_200};
    font-weight: ${theme.fontWeights.normal};
    line-height: 24px;
    letter-spacing: 0.2px;
  }
`;

const InputWrapper = styled.div`
  width: 60%;
  margin: 0 auto;
  & > * + * {
    margin-top: 24px;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 40px;
  margin-bottom: 26px;
  .logo {
    width: 157px;
    height: 102px;
  }
`;

const CheckboxWrapper = styled(Checkbox)`
  margin-top: 12px;
`;

const FooterFormWrapper = styled.div`
  width: 60%;
  margin: 0 auto;
  margin-top: 32px;
  & > * + * {
    margin-top: 12px;
  }
  .submit-btn {
    width: 100%;
  }
  .forget-text {
    font-size: ${theme.fontSizes.normal};
    font-weight: ${theme.fontWeights.medium};
    line-height: 24px; /* 171.429% */
    letter-spacing: 0.2px;
    color: ${theme.colors.dark_200};
    text-align: center;
    cursor: pointer;
  }
`;
